import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn,
  SyutSelect, type SelectOption, ColumnSelector, useColumnVisibility, type ColumnDefinition,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useServices } from 'hooks/api/useCommercial';
import {
  serviceService, serviceCategoryService, coaLedgerService,
  taxMetaService, uomService, entityTaxConfigService, taxClassificationService,
} from 'api/services';
import taxMasterService from 'api/services/taxMasterService';
import TaxMasterPicker from 'components/shared/tax-master-module/TaxMasterPicker';
import TaxFieldsPanel, { validateTaxFields, type AsyncOptionSource, type TaxFieldValues } from 'components/modules/tax/TaxFieldsPanel';
import { DEV_BU } from 'config/devNavigation';
import type {
  ServiceDto, ServiceQueryParams, CreateServiceRequest,
  ServiceBuDescriptionInput, TaxFieldContextDto, ServiceTaxMappingDto, UpsertServiceTaxMappingRequest,
} from 'api/types';

// ============================================================================
// SERVICE MASTER — the thin item master (ADR-0018) with its TAX MAPPING (ADR-0047 Phase 2 §7):
//
//   • every item under a jurisdiction is pinned to a TAX MASTER (rate_code) through the shared
//     picker — no "tax applicable" flag: a non-taxable item is one pinned to an EXEMPT master;
//   • the jurisdiction's SERVICE field schema (HSN/SAC + unit under GST, nothing under
//     Botswana VAT) renders through TaxFieldsPanel; values travel in `attributes`;
//   • the mapping is saved IN THE SAME REQUEST as the item (one transaction server-side) —
//     the former second call, with its swallowed failure, is what left items unmapped;
//   • a new item is pre-filled with the entity's default master (Tax Settings → Defaults);
//     an item with no master is shown as UNMAPPED and refused when a document is posted.
// ============================================================================

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

// Race a promise against a timeout so a slow/hung backend can't lock the UI.
const withTimeout = <T,>(p: Promise<T>, ms: number, fallback: T): Promise<T> =>
  Promise.race([p, new Promise<T>(res => setTimeout(() => res(fallback), ms))]);

// Module-level cache for the SERVICE tax field context (jurisdiction + field schema + units).
// Stable for a session; invalidated by `invalidateTaxContext()` when the screen re-mounts so a
// jurisdiction change under Tax Settings is picked up on the next visit.
const taxContextCache: { promise: Promise<TaxFieldContextDto | null> | null; value: TaxFieldContextDto | null } = {
  promise: null,
  value: null,
};
const invalidateTaxContext = () => { taxContextCache.value = null; taxContextCache.promise = null; };
const loadTaxContext = (): Promise<TaxFieldContextDto | null> => {
  if (taxContextCache.value) return Promise.resolve(taxContextCache.value);
  if (taxContextCache.promise) return taxContextCache.promise;
  taxContextCache.promise = withTimeout(taxMetaService.getFieldContext('SERVICE'), 6000, null as any)
    .catch(() => null)
    .then(ctx => { taxContextCache.value = ctx; taxContextCache.promise = null; return ctx; });
  return taxContextCache.promise;
};
const ITEM_TYPE_OPTIONS: SelectOption[] = [
  { value: 'SERVICE', label: 'Service' },
  { value: 'REIMBURSEMENT', label: 'Reimbursement' },
];

const CLASSIFICATION_BINDING = 'LINE.SAC_HSN_CODE';
const UNIT_BINDING = 'LINE.UNIT';
const CLASSIFICATION_SOURCE = 'TAX.CLASSIFICATION';

interface FormData {
  serviceCode: string; serviceName: string; itemType: string;
  serviceCategoryId: string; incomeLedgerId: string;
  uom: string; defaultRate: string; description: string; statusCode: string;
}
const EMPTY: FormData = {
  serviceCode: '', serviceName: '', itemType: 'SERVICE', serviceCategoryId: '', incomeLedgerId: '',
  uom: '', defaultRate: '', description: '', statusCode: 'ACTIVE',
};

/** The item's tax mapping as edited: the master plus the schema values. */
interface TaxMappingForm {
  rateCode: string;
  rateName: string;
  rowVersion: number;
  values: TaxFieldValues;
}
const EMPTY_TAX: TaxMappingForm = { rateCode: '', rateName: '', rowVersion: 0, values: {} };

const mappingToForm = (m?: ServiceTaxMappingDto | null, keepVersion = true): TaxMappingForm => m
  ? { rateCode: m.rateCode || '', rateName: m.rateName || '', rowVersion: keepVersion ? m.rowVersion : 0, values: { ...(m.attributes || {}) } }
  : { ...EMPTY_TAX };

// ============================================================================
// FORM MODAL
// ============================================================================

interface ServiceFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record?: ServiceDto | null;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => void;
}

const ServiceFormModal: React.FC<ServiceFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, createInstanceId, minimizeRef, onRestoreModal,
}) => {
  const { minimize } = useModalMinimize();
  const [form, setForm] = useState<FormData>(EMPTY);
  const [invoiceDescription, setInvoiceDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const snapshotRef = useRef('');
  const isRestoreRef = useRef(false);
  const otherBuDescriptions = useRef<ServiceBuDescriptionInput[]>([]);
  const catCache = useRef<Map<string, string>>(new Map());
  const ledgerCache = useRef<Map<string, string>>(new Map());
  const ledgerTypeCache = useRef<Map<string, string | null | undefined>>(new Map());
  const uomLabelCache = useRef<Map<string, string>>(new Map());
  // Classification code → "CODE — description" for the async select's seed label, and the
  // default unit the master suggests for it.
  const classificationCache = useRef<Map<string, { label: string; defaultUnitCode?: string | null }>>(new Map());
  const [taxContext, setTaxContext] = useState<TaxFieldContextDto | null>(taxContextCache.value);
  const [tax, setTax] = useState<TaxMappingForm>(EMPTY_TAX);

  const modeRef = useRef(mode); modeRef.current = mode;
  const recordRef = useRef(record); recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const formRef = useRef(form); formRef.current = form;
  const descRef = useRef(invoiceDescription); descRef.current = invoiceDescription;
  const taxRef = useRef(tax); taxRef.current = tax;

  const classificationField = useMemo(
    () => taxContext?.fields.find(f => (f.binding || '').toUpperCase() === CLASSIFICATION_BINDING) || null,
    [taxContext]);
  const unitField = useMemo(
    () => taxContext?.fields.find(f => (f.binding || '').toUpperCase() === UNIT_BINDING) || null,
    [taxContext]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }
    const src = (mode === 'edit' || mode === 'copy') ? record : null;
    let d: FormData;
    if (src) {
      d = {
        // Copy mode: blank code (user must supply a new one)
        serviceCode: mode === 'copy' ? '' : src.serviceCode,
        serviceName: src.serviceName,
        itemType: src.itemType || 'SERVICE',
        serviceCategoryId: src.serviceCategoryId || '',
        incomeLedgerId: src.incomeLedgerId || '',
        uom: src.uom || '',
        defaultRate: src.defaultRate != null ? String(src.defaultRate) : '',
        description: src.description || '',
        statusCode: src.statusCode ?? 'ACTIVE',
      };
      if (src.serviceCategoryId && src.serviceCategoryName) {
        catCache.current.set(src.serviceCategoryId, src.serviceCategoryName);
      }
      if (src.incomeLedgerId && src.incomeLedgerName) {
        ledgerCache.current.set(src.incomeLedgerId, `${src.incomeLedgerCode} - ${src.incomeLedgerName}`);
      }
    } else {
      d = { ...EMPTY };
    }
    otherBuDescriptions.current = [];
    setForm(d); setInvoiceDescription(''); setTaxContext(taxContextCache.value); setTax(EMPTY_TAX);
    snapshotRef.current = ''; setErrors({});
    setLoadingData(true);
    (async () => {
      try {
        const isEdit = !!src && mode === 'edit';
        const [full, ctx, defaults] = await Promise.all([
          src ? withTimeout(serviceService.getById(src.id).catch(() => null), 6000, null) : Promise.resolve(null),
          loadTaxContext(),
          // A NEW item starts from the entity's default master (a pre-fill, never a fallback).
          mode === 'create' ? withTimeout(entityTaxConfigService.get().catch(() => null), 6000, null) : Promise.resolve(null),
        ]);

        let myDesc = '';
        if (isEdit) {
          const others: ServiceBuDescriptionInput[] = [];
          (full?.buDescriptions || src!.buDescriptions || []).forEach(b => {
            if (b.businessUnitId === DEV_BU.id) myDesc = b.description;
            else others.push({ businessUnitId: b.businessUnitId, description: b.description });
          });
          otherBuDescriptions.current = others;
        }

        let t: TaxMappingForm = EMPTY_TAX;
        if (ctx?.jurisdiction) {
          const mapping = full?.taxMapping ?? src?.taxMapping ?? null;
          if (mapping) {
            t = mappingToForm(mapping, isEdit); // a copy starts a NEW mapping row
            if (mapping.classificationCode) {
              classificationCache.current.set(mapping.classificationCode, {
                label: mapping.classificationDescription ? `${mapping.classificationCode} — ${mapping.classificationDescription}` : mapping.classificationCode,
              });
            }
          } else if (mode === 'create' && defaults?.defaultRateCode) {
            t = { ...EMPTY_TAX, rateCode: defaults.defaultRateCode, rateName: defaults.defaultRateName || '' };
          }
        }
        setInvoiceDescription(myDesc); setTaxContext(ctx); setTax(t);
        snapshotRef.current = JSON.stringify({ d, bu: myDesc, t });
      } finally {
        setLoadingData(false);
      }
    })();
  }, [isOpen]);

  const isDirty = useMemo(
    () => snapshotRef.current
      ? JSON.stringify({ d: form, bu: invoiceDescription, t: tax }) !== snapshotRef.current
      : false,
    [form, invoiceDescription, tax],
  );
  const upd = (patch: Partial<FormData>) => setForm(p => ({ ...p, ...patch }));
  const clearErr = (f: string) => setErrors(p => { if (!p[f]) return p; const n = { ...p }; delete n[f]; return n; });
  const handleClose = () => { if (isDirty) setDiscardOpen(true); else onClose(); };

  const updTaxValue = useCallback((key: string, val: unknown) => {
    clearErr(`tax.${key}`);
    setTax(prev => {
      const values = { ...prev.values, [key]: val };
      // Picking a classification the master knows suggests its default unit when none is chosen.
      if (classificationField && key === classificationField.fieldKey && unitField && typeof val === 'string' && val) {
        const known = classificationCache.current.get(val.toUpperCase());
        if (known?.defaultUnitCode && !values[unitField.fieldKey]) values[unitField.fieldKey] = known.defaultUnitCode;
      }
      return { ...prev, values };
    });
  }, [classificationField, unitField]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentMode = modeRef.current;
    const currentRecord = recordRef.current;
    const currentInstanceId = createInstanceIdRef.current;
    const savedForm = { ...formRef.current };
    const savedDesc = descRef.current;
    const savedTax = { ...taxRef.current, values: { ...taxRef.current.values } };
    const savedSnapshot = snapshotRef.current;
    const dirty = savedSnapshot
      ? JSON.stringify({ d: savedForm, bu: savedDesc, t: savedTax }) !== savedSnapshot
      : false;

    let modalId: string;
    let stripTitle: string;
    if (currentMode === 'create') {
      const idx = currentInstanceId || '1';
      modalId = `${tabId}-form-modal-create-${idx}`;
      const label = savedForm.serviceCode.trim() || savedForm.serviceName.trim();
      stripTitle = label ? `Add - ${label} (${idx})` : `Add New (${idx})`;
    } else if (currentMode === 'copy') {
      const key = currentRecord?.serviceCode || currentInstanceId || '1';
      modalId = `${tabId}-form-modal-copy-${key}`;
      const label = savedForm.serviceCode.trim() || currentRecord?.serviceCode || '';
      stripTitle = label ? `Copy - ${label}` : 'Copy Service';
    } else {
      const key = currentRecord?.serviceCode || currentRecord?.id || '';
      modalId = `${tabId}-form-modal-edit-${key}`;
      stripTitle = `Edit - ${currentRecord?.serviceCode || currentRecord?.serviceName || 'Service'}`;
    }

    minimize({
      id: modalId, title: stripTitle, moduleName, tabId, isDirty: dirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setForm(savedForm); setInvoiceDescription(savedDesc); setTax(savedTax);
        snapshotRef.current = savedSnapshot;
        onRestoreModal?.(
          currentMode,
          currentMode === 'create' ? currentInstanceId : undefined,
          currentMode !== 'create' ? currentRecord?.serviceCode : undefined,
        );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  useEffect(() => {
    if (minimizeRef) minimizeRef.current = handleMinimize;
  }, [minimizeRef, handleMinimize]);

  const loadCategories = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await serviceCategoryService.getAll({ pageSize: 50, searchTerm: input || undefined });
      res.data.forEach(c => catCache.current.set(c.id, `${c.categoryCode} - ${c.categoryName}`));
      return res.data.map(c => ({ value: c.id, label: `${c.categoryCode} - ${c.categoryName}` }));
    } catch {
      return [];
    }
  }, []);

  const loadLedgers = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await coaLedgerService.getAll({ pageSize: 50, searchTerm: input || undefined });
      res.data.forEach(l => {
        ledgerCache.current.set(l.id, `${l.ledgerCode} - ${l.ledgerName}`);
        ledgerTypeCache.current.set(l.id, l.accountTypeCode);
      });
      return res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }));
    } catch {
      return [];
    }
  }, []);

  // Persist the loose uom_code string. Label shape is "CODE — Name (symbol)". This is the
  // COMMERCIAL unit (common.tbl_uom) the item is priced in; the STATUTORY unit a regime reports
  // quantities in, where it declares one, is a tax field below (tax.tbl_unit_of_measure).
  const loadUomOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const list = await uomService.getAll(undefined, input || undefined);
      return list.map(u => {
        const label = u.symbol ? `${u.uomCode} — ${u.uomName} (${u.symbol})` : `${u.uomCode} — ${u.uomName}`;
        uomLabelCache.current.set(u.uomCode, label);
        return { value: u.uomCode, label };
      });
    } catch {
      return [];
    }
  }, []);

  // TAX.CLASSIFICATION: searched through the classification master; free entry unless the
  // entity enforces the master (the server refuses an unknown code in that case).
  const asyncSources = useMemo<Record<string, AsyncOptionSource>>(() => ({
    [CLASSIFICATION_SOURCE]: {
      load: async (input: string) => {
        try {
          const rows = await taxClassificationService.search(input);
          return rows.map(r => {
            const label = `${r.code} — ${r.description}`;
            classificationCache.current.set(r.code, { label, defaultUnitCode: r.defaultUnitCode });
            return { value: r.code, label };
          });
        } catch {
          return [];
        }
      },
      labelFor: (value: string) => classificationCache.current.get(value.toUpperCase())?.label,
      allowFreeEntry: !taxContext?.classificationEnforced,
    },
  }), [taxContext?.classificationEnforced]);

  const handleSave = async () => {
    const e: Record<string, string> = {};
    if (!form.serviceCode.trim()) e.serviceCode = 'Code is required';
    if (!form.serviceName.trim()) e.serviceName = 'Name is required';
    if (taxContext?.jurisdiction) {
      if (!tax.rateCode) e.rateCode = 'Choose the tax master this item is taxed under.';
      const taxErrors = validateTaxFields(taxContext, tax.values);
      Object.entries(taxErrors).forEach(([k, v]) => { e[`tax.${k}`] = v; });
    }
    if (Object.keys(e).length) { setErrors(e); return; }
    if (form.itemType === 'REIMBURSEMENT' && !form.incomeLedgerId) {
      notificationService.toast.warning('A reimbursement item requires a ledger.');
      return;
    }
    try {
      const excludeId = mode === 'edit' ? record?.id : undefined;
      const ok = await serviceService.isCodeAvailable(form.serviceCode.trim(), excludeId);
      if (!ok) { setErrors({ serviceCode: 'This code already exists.' }); return; }
    } catch { /* server enforce */ }
    setErrors({}); setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    setConfirmOpen(false); setSaving(true);
    try {
      const buDescriptions: ServiceBuDescriptionInput[] = [...otherBuDescriptions.current];
      if (invoiceDescription.trim()) {
        buDescriptions.push({ businessUnitId: DEV_BU.id, description: invoiceDescription.trim() });
      }
      // The mapping travels WITH the item: one request, one transaction. Without a
      // jurisdiction there is nothing to map to and the server saves the item alone.
      const taxMapping: UpsertServiceTaxMappingRequest | undefined = taxContext?.jurisdiction
        ? { rateCode: tax.rateCode, attributes: tax.values, rowVersion: tax.rowVersion }
        : undefined;
      const payload: CreateServiceRequest = {
        serviceCode: form.serviceCode.trim(), serviceName: form.serviceName.trim(),
        itemType: form.itemType, serviceCategoryId: form.serviceCategoryId || undefined,
        incomeLedgerId: form.incomeLedgerId || undefined, uom: form.uom.trim() || undefined,
        defaultRate: form.defaultRate ? Number(form.defaultRate) : undefined,
        description: form.description.trim() || undefined, statusCode: form.statusCode, buDescriptions,
        taxMapping,
      };
      if (mode === 'edit' && record) {
        await serviceService.update(record.id, { ...payload, rowVersion: record.rowVersion });
      } else {
        await serviceService.create(payload);
      }
      setSaving(false);
      const verb = mode === 'edit' ? 'updated' : 'created';
      notificationService.toast.success(`Service "${form.serviceName}" ${verb}.`);
      onSaved(); onClose();
    } catch (err: any) {
      setSaving(false);
      const data = err?.response?.data;
      // The server's field-schema validator lists every problem; show them on the tax panel.
      if (data?.errorCode === 'TAX_FIELD_VALIDATION' && Array.isArray(data?.errors) && data.errors.length) {
        notificationService.toast.error(data.errors.join(' '));
      } else {
        notificationService.toast.error(data?.message || 'Could not save.');
      }
    }
  };

  const ledgerTypeOf = form.incomeLedgerId ? ledgerTypeCache.current.get(form.incomeLedgerId) : undefined;
  const ledgerWarn = form.itemType === 'SERVICE' && !!form.incomeLedgerId
    && !!ledgerTypeOf && ledgerTypeOf !== 'INCOME';

  if (!isOpen) return null;
  const errCls = (f: string) => `form-input${errors[f] ? ' form-field-error' : ''}`;
  const headerText = mode === 'edit' ? 'Edit Service' : mode === 'copy' ? 'Copy Service' : 'Add New Service';
  const jurisdiction = taxContext?.jurisdiction || null;
  const taxErrors: Record<string, string> = {};
  Object.entries(errors).forEach(([k, v]) => { if (k.startsWith('tax.')) taxErrors[k.slice(4)] = v; });

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {(saving || loadingData) && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: '0.75rem', zIndex: 10, borderRadius: 'inherit',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {saving ? 'Saving…' : 'Loading…'}
            </span>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{headerText}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={saving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>
        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="serviceCode">
              <label className="form-label">Code <span className="text-danger">*</span></label>
              <input className={errCls('serviceCode')} value={form.serviceCode} maxLength={30}
                readOnly={mode === 'edit'}
                onChange={e => { clearErr('serviceCode'); upd({ serviceCode: e.target.value.toUpperCase() }); }}
                style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : {}} />
              {errors.serviceCode && <div className="form-error-message">{errors.serviceCode}</div>}
            </div>
            <div className="form-group" data-field="serviceName">
              <label className="form-label">Name <span className="text-danger">*</span></label>
              <input className={errCls('serviceName')} value={form.serviceName} maxLength={200}
                onChange={e => { clearErr('serviceName'); upd({ serviceName: e.target.value }); }} />
              {errors.serviceName && <div className="form-error-message">{errors.serviceName}</div>}
            </div>
            <div className="form-group">
              <SyutSelect label="Type" options={ITEM_TYPE_OPTIONS} value={form.itemType}
                onChange={v => upd({ itemType: (v as string) || 'SERVICE' })}
                isClearable={false} menuPortalTarget={document.body} />
            </div>
            <div className="form-group">
              <SyutSelect label="Category" isAsync loadOptions={loadCategories} defaultOptions cacheOptions={false}
                seedOptions={form.serviceCategoryId
                  ? [{ value: form.serviceCategoryId, label: catCache.current.get(form.serviceCategoryId) || '' }]
                  : undefined}
                menuPortalTarget={document.body} value={form.serviceCategoryId}
                onChange={v => upd({ serviceCategoryId: (v as string) || '' })}
                placeholder="None" isClearable />
            </div>
            <div className="form-group">
              <SyutSelect
                label={form.itemType === 'REIMBURSEMENT'
                  ? <>Reimbursement Ledger <span className="text-danger">*</span></>
                  : 'Income Ledger'}
                isAsync loadOptions={loadLedgers} defaultOptions cacheOptions={false}
                seedOptions={form.incomeLedgerId
                  ? [{ value: form.incomeLedgerId, label: ledgerCache.current.get(form.incomeLedgerId) || '' }]
                  : undefined}
                menuPortalTarget={document.body} value={form.incomeLedgerId}
                onChange={v => upd({ incomeLedgerId: (v as string) || '' })}
                placeholder={form.itemType === 'REIMBURSEMENT'
                  ? 'Earmarked non-revenue ledger'
                  : 'Revenue ledger'}
                isClearable />
            </div>
            <div className="form-group">
              <SyutSelect label="UOM" isAsync loadOptions={loadUomOptions}
                defaultOptions cacheOptions={false}
                seedOptions={form.uom
                  ? [{ value: form.uom, label: uomLabelCache.current.get(form.uom) || form.uom }]
                  : undefined}
                menuPortalTarget={document.body}
                value={form.uom}
                onChange={v => upd({ uom: (v as string) || '' })}
                placeholder="Select unit" isClearable />
            </div>
            <div className="form-group">
              <label className="form-label">Default Rate</label>
              <input type="number" className="form-input" value={form.defaultRate}
                onChange={e => upd({ defaultRate: e.target.value })} />
            </div>
            <div className="form-group">
              <SyutSelect label="Status" options={STATUS_OPTIONS} value={form.statusCode}
                onChange={v => upd({ statusCode: (v as string) || '' })}
                isClearable={false} menuPortalTarget={document.body} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" rows={2} value={form.description}
              onChange={e => upd({ description: e.target.value })} />
          </div>

          {/* Tax — the master this item is taxed under, plus what the jurisdiction declares. */}
          {jurisdiction && (
            <div style={{
              border: '1px solid var(--form-border-color)', borderRadius: '0.5rem',
              padding: '0.75rem 1rem 0.5rem', marginTop: '0.5rem', background: 'var(--bg-secondary)',
            }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>
                Tax · {jurisdiction.jurisdictionName}
              </div>
              <div className="form-group" data-field="rateCode">
                <label className="form-label">Tax master <span className="text-danger">*</span></label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <div className={`form-input${errors.rateCode ? ' form-field-error' : ''}`} style={{ flex: 1, minHeight: 38, display: 'flex', alignItems: 'center', background: 'var(--bg-primary)' }}>
                    {tax.rateCode
                      ? <span><strong>{tax.rateName || tax.rateCode}</strong> <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono, monospace)', fontSize: '0.8rem' }}>{tax.rateCode}</span></span>
                      : <span style={{ color: 'var(--text-muted)' }}>Unmapped — choose a master</span>}
                  </div>
                  <button type="button" className="btn btn-secondary btn-sm" disabled={saving} onClick={() => setPickerOpen(true)}>Choose…</button>
                  {tax.rateCode && <button type="button" className="btn btn-link btn-sm" disabled={saving} onClick={() => setTax(p => ({ ...p, rateCode: '', rateName: '' }))}>Clear</button>}
                </div>
                {errors.rateCode
                  ? <div className="form-error-message">{errors.rateCode}</div>
                  : <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Every document line for this item is priced by this master. A non-taxable item is pinned to an exempt master.</div>}
              </div>
              <TaxFieldsPanel context={taxContext} values={tax.values} bare disabled={saving} errors={taxErrors}
                asyncSources={asyncSources} onChange={updTaxValue} />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Invoice Default Description</label>
            <textarea className="form-textarea" rows={3} value={invoiceDescription}
              placeholder="Default description copied onto invoice lines for this service (multi-line)…"
              onChange={e => setInvoiceDescription(e.target.value)} />
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              Applies to your current business unit. Other BUs' descriptions are preserved.
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <IconSystem.check size={16} /> {mode === 'edit' ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
      {jurisdiction && (
        <TaxMasterPicker
          isOpen={pickerOpen}
          title="Choose the tax master for this item"
          recentlyUsedKey="service-master.tax-master"
          load={(q) => taxMasterService.list(jurisdiction.jurisdictionCode, q)}
          onSelect={(m) => { clearErr('rateCode'); setTax(p => ({ ...p, rateCode: m.rateCode, rateName: m.name })); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
      <ConfirmDialog open={confirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`${mode === 'edit' ? 'Save changes to' : 'Create'} ${form.itemType === 'REIMBURSEMENT' ? 'reimbursement item' : 'service'} "${form.serviceName}"?${jurisdiction && tax.rateCode ? `\n\nTax master: ${tax.rateName || tax.rateCode}.` : ''}${ledgerWarn ? `\n\n⚠ This Service maps to a non-Income ledger (${ledgerTypeOf}). Continue?` : ''}`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'} cancelLabel="Cancel"
        variant={ledgerWarn ? 'danger' : 'default'}
        onConfirm={handleConfirmSave} onCancel={() => setConfirmOpen(false)} />
      <ConfirmDialog open={discardOpen} title="Discard Changes?"
        message="You have unsaved changes. Discard them?" confirmLabel="Discard"
        cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)} />
    </>
  );
};

// ============================================================================
// VIEW MODAL (canonical read-only view — follows the template's
// ServiceMasterViewModal pattern: fixed "Service Details" title, ViewField
// helper, 2-col grid, status-as-badge, minimize support, optional Edit CTA.
// ============================================================================

const ViewField: React.FC<{ label: string; value?: string | null; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label className="form-label">{label}</label>
    <div style={{
      padding: '0.5rem',
      background: 'var(--bg-secondary)',
      borderRadius: '0.375rem',
      border: '1px solid var(--form-border-color)',
      fontSize: '0.875rem',
      minHeight: '36px',
      whiteSpace: 'pre-wrap',
    }}>
      {value ? value : <span style={{ color: 'var(--text-muted)' }}>—</span>}
    </div>
  </div>
);

export interface ServiceMasterViewModalProps {
  isOpen: boolean;
  service: ServiceDto | null;
  onClose: () => void;
  onEdit?: (service: ServiceDto) => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (serviceCode: string) => void;
}

const ServiceMasterViewModal: React.FC<ServiceMasterViewModalProps> = ({
  isOpen, service, onClose, onEdit, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();
  const [taxCtx, setTaxCtx] = useState<TaxFieldContextDto | null>(taxContextCache.value);
  const [full, setFull] = useState<ServiceDto | null>(null);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (!isOpen || !service) { setTaxCtx(taxContextCache.value); setFull(null); setLoadingData(false); return; }
    setLoadingData(true);
    (async () => {
      try {
        const [ctx, detail] = await Promise.all([
          loadTaxContext(),
          withTimeout(serviceService.getById(service.id).catch(() => null), 6000, null),
        ]);
        setTaxCtx(ctx); setFull(detail);
      } finally {
        setLoadingData(false);
      }
    })();
  }, [isOpen, service]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !service) return;
    minimize({
      id: `${tabId}-view-modal-${service.serviceCode}`,
      title: `View - ${service.serviceCode}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(service.serviceCode),
    });
    onClose();
  }, [tabId, moduleName, service, minimize, onClose, onRestoreView]);

  if (!isOpen || !service) return null;

  const itemTypeLabel = service.itemType === 'REIMBURSEMENT' ? 'Reimbursement' : 'Service';
  const ledgerDisplay = service.incomeLedgerCode
    ? (service.incomeLedgerName ? `${service.incomeLedgerCode} — ${service.incomeLedgerName}` : service.incomeLedgerCode)
    : null;
  const rateDisplay = service.defaultRate != null
    ? service.defaultRate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;

  const showTaxPanel = !!(taxCtx?.jurisdiction);
  const taxMap = full?.taxMapping ?? service.taxMapping ?? null;
  const masterDisplay = taxMap?.rateCode ? `${taxMap.rateName || taxMap.rateCode} (${taxMap.rateCode})` : null;
  const schemaFields = (taxCtx?.fields || []).filter(f => (f.binding || '').toUpperCase() !== CLASSIFICATION_BINDING && (f.binding || '').toUpperCase() !== UNIT_BINDING);
  const classificationField = taxCtx?.fields.find(f => (f.binding || '').toUpperCase() === CLASSIFICATION_BINDING);
  const unitField = taxCtx?.fields.find(f => (f.binding || '').toUpperCase() === UNIT_BINDING);
  const unitName = taxMap?.unitCode ? (taxCtx?.units || []).find(u => u.unitCode === taxMap.unitCode)?.unitName : undefined;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {loadingData && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: '0.75rem', zIndex: 10, borderRadius: 'inherit',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Loading…</span>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}
        <div className="modal-header">
          <h2 className="modal-title">Service Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={loadingData}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose} disabled={loadingData}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <ViewField label="Code" value={service.serviceCode} />
            <div>
              <label className="form-label">Status</label>
              <div style={{ paddingTop: '4px' }}>
                <span className="badge">
                  {service.statusName || service.statusCode || 'Active'}
                </span>
              </div>
            </div>
            <ViewField label="Name" value={service.serviceName} fullWidth />
            <ViewField label="Type" value={itemTypeLabel} />
            <ViewField label="Category" value={service.serviceCategoryName} />
            <ViewField label="Income Ledger" value={ledgerDisplay} fullWidth />
            <ViewField label="UOM" value={service.uom} />
            <ViewField label="Default Rate" value={rateDisplay} />
            <ViewField label="Description" value={service.description} fullWidth />

            {showTaxPanel && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{
                  border: '1px solid var(--form-border-color)',
                  borderRadius: '0.5rem',
                  padding: '0.75rem 1rem 0.5rem',
                  marginTop: '0.25rem',
                  background: 'var(--bg-secondary)',
                }}>
                  <div style={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'var(--text-secondary)',
                    marginBottom: '0.6rem',
                  }}>
                    Tax · {taxCtx!.jurisdiction!.jurisdictionName}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label className="form-label">Tax master</label>
                      <div style={{ padding: '0.5rem', background: 'var(--bg-primary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)', fontSize: '0.875rem', minHeight: 36 }}>
                        {masterDisplay || <span className="badge" style={{ background: 'var(--danger-bg, #fee2e2)', color: 'var(--danger, #b91c1c)' }}>Unmapped — cannot be invoiced</span>}
                      </div>
                    </div>
                    {classificationField && (
                      <ViewField label={classificationField.label}
                        value={taxMap?.classificationCode ? (taxMap.classificationDescription ? `${taxMap.classificationCode} — ${taxMap.classificationDescription}` : taxMap.classificationCode) : null} />
                    )}
                    {unitField && (
                      <ViewField label={unitField.label} value={taxMap?.unitCode ? (unitName ? `${taxMap.unitCode} — ${unitName}` : taxMap.unitCode) : null} />
                    )}
                    {schemaFields.map(f => (
                      <ViewField key={f.fieldKey} label={f.label} value={taxMap?.attributes?.[f.fieldKey] != null ? String(taxMap.attributes[f.fieldKey]) : null} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            <ViewField label="Created By" value={service.createdByName} />
            <ViewField
              label="Created At"
              value={service.createdAt ? new Date(service.createdAt).toLocaleString() : null}
            />
            {service.updatedAt && (
              <>
                <ViewField label="Updated By" value={service.updatedByName} />
                <ViewField
                  label="Updated At"
                  value={new Date(service.updatedAt).toLocaleString()}
                />
              </>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => { onClose(); onEdit(service); }}>
              <IconSystem.edit size={16} /> Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ============================================================================
// SCREEN
// ============================================================================

type ViewMode = 'list' | 'grid';

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'serviceCode', label: 'Code' },
  { id: 'serviceName', label: 'Name' },
  { id: 'itemType', label: 'Type' },
  { id: 'category', label: 'Category' },
  { id: 'taxMaster', label: 'Tax Master' },
  { id: 'rate', label: 'Default Rate' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

const fmtMoney = (n?: number | null) =>
  n != null ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

interface ServiceMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const ServiceMasterScreen: React.FC<ServiceMasterScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('service-master');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  // Re-read the tax context on each mount so a jurisdiction change under Tax Settings shows up
  // on the next visit; the modals then hit the warm cache.
  const [hasJurisdiction, setHasJurisdiction] = useState<boolean>(!!taxContextCache.value?.jurisdiction);
  useEffect(() => {
    invalidateTaxContext();
    void loadTaxContext().then(ctx => setHasJurisdiction(!!ctx?.jurisdiction));
  }, []);

  const [queryParams, setQueryParams] = useState<ServiceQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useServices(queryParams);
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('service-master-columns', COLUMN_DEFS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [itemTypeFilter, setItemTypeFilter] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selected, setSelected] = useState<ServiceDto | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewService, setViewService] = useState<ServiceDto | null>(null);

  const handleAdd = useCallback(() => {
    let next = 1;
    if (tabId) {
      while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++;
    }
    setCreateInstanceId(String(next));
    setFormMode('create'); setSelected(null); setFormOpen(true);
  }, [tabId, isMinimized]);

  const handleEdit = useCallback((r: ServiceDto) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${r.serviceCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    setFormMode('edit'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleView = useCallback((r: ServiceDto) => {
    const viewId = tabId ? `${tabId}-view-modal-${r.serviceCode}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId); modal?.onRestore?.();
      return;
    }
    setViewService(r); setViewOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((serviceCode: string) => {
    const rec = data.find(r => r.serviceCode === serviceCode);
    if (rec) { setViewService(rec); setViewOpen(true); }
  }, [data]);

  const handleCopy = useCallback((r: ServiceDto) => {
    const modalId = tabId ? `${tabId}-form-modal-copy-${r.serviceCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    if (formOpen) formModalMinimizeRef.current?.();
    setFormMode('copy'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal, formOpen]);

  const handleRestoreModal = useCallback(
    (m: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => {
      if (m === 'create' && instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(r => r.serviceCode === recordCode);
        if (rec) setSelected(rec);
      }
      setFormMode(m); setFormOpen(true);
    },
    [data],
  );

  const handleDelete = useCallback(async (r: ServiceDto) => {
    const ok = await confirmDelete({ resourceLabel: r.serviceName });
    if (!ok) return;
    try {
      await serviceService.delete(r.id, r.rowVersion);
      notificationService.toast.success(`Service "${r.serviceName}" deleted.`);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not delete.');
    }
  }, [confirmDelete, refetch]);

  const columns: SyutGridColumn<ServiceDto>[] = useMemo(() => [
    { id: 'serviceCode', header: 'Code', accessorKey: 'serviceCode', size: 120 },
    { id: 'serviceName', header: 'Name', accessorKey: 'serviceName', size: 240 },
    {
      id: 'itemType', header: 'Type', accessorKey: 'itemType', size: 130,
      cell: ({ row }) => {
        const t = (row.original as ServiceDto).itemType;
        return (
          <span className="badge" style={t === 'REIMBURSEMENT' ? { background: 'var(--bg-secondary)' } : {}}>
            {t === 'REIMBURSEMENT' ? 'Reimbursement' : 'Service'}
          </span>
        );
      },
    },
    {
      id: 'category', header: 'Category', accessorKey: 'serviceCategoryName', size: 180,
      cell: ({ row }) => (row.original as ServiceDto).serviceCategoryName || '—',
    },
    {
      id: 'taxMaster', header: 'Tax Master', size: 180,
      cell: ({ row }) => {
        const m = (row.original as ServiceDto).taxMapping;
        if (!hasJurisdiction) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
        if (!m?.rateCode) {
          return <span className="badge" title="Refused on documents until mapped" style={{ background: 'var(--danger-bg, #fee2e2)', color: 'var(--danger, #b91c1c)' }}>Unmapped</span>;
        }
        return (
          <span title={m.rateCode}>
            {m.rateName || m.rateCode}
            {m.classificationCode && <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}> · {m.classificationCode}</span>}
          </span>
        );
      },
    },
    {
      id: 'rate', header: 'Default Rate', accessorKey: 'defaultRate', size: 120,
      cell: ({ row }) => (
        <span style={{ display: 'block', textAlign: 'right' }}>
          {fmtMoney((row.original as ServiceDto).defaultRate)}
        </span>
      ),
    },
    {
      id: 'status', header: 'Status', accessorKey: 'statusCode', size: 110,
      cell: ({ row }) => (
        <span className="badge">{(row.original as ServiceDto).statusName || 'Active'}</span>
      ),
    },
  ], [hasJurisdiction]);

  const rowActions = useCallback((r: ServiceDto): ActionMenuItem[] => {
    // View is always available (no gate) — anyone with grid access can view.
    // Order matches template convention: View → Copy → Edit → Delete.
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(r) },
    ];
    if (can('UPDATE')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(r) });
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(r) });
    }
    if (can('DELETE')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(r), variant: 'danger' });
    }
    return items;
  }, [can, handleView, handleEdit, handleCopy, handleDelete]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, 'Add Service'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  return (
    <EnterpriseScreenShell
      title="Services"
      description="Thin service master with BU-wise descriptions and the tax master each item is taxed under"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.file size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search name / code...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{
        onClick: () => setFilterOpen(!filterOpen),
        isActive: filterOpen,
        appliedCount: [itemTypeFilter, includeInactive ? '1' : ''].filter(Boolean).length,
      }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Type"
                  options={[
                    { value: '', label: 'All' },
                    { value: 'SERVICE', label: 'Service' },
                    { value: 'REIMBURSEMENT', label: 'Reimbursement' },
                  ]}
                  value={itemTypeFilter}
                  onChange={v => setItemTypeFilter((v as string) || '')}
                  isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeInactive}
                    onChange={e => setIncludeInactive(e.target.checked)} /> Include inactive
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => {
                setItemTypeFilter(''); setIncludeInactive(false);
                setQueryParams(p => ({ page: 1, pageSize: p.pageSize }));
              }}>Clear</button>
              <button className="btn btn-primary" onClick={() => {
                setQueryParams(p => ({
                  ...p,
                  itemType: itemTypeFilter || undefined,
                  includeInactive: includeInactive || undefined,
                  page: 1,
                }));
                setFilterOpen(false);
              }}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={columns} enableRowSelection
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            frozenLeftColumns={['select', 'serviceCode']} frozenRightColumns={['actions']}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No services yet — click Add.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.map(r => (
                <div key={r.id} style={{
                  background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{r.serviceCode}</div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0' }}>{r.serviceName}</h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                    <span>
                      {r.itemType === 'REIMBURSEMENT' ? 'Reimbursement' : 'Service'}
                      {r.serviceCategoryName ? ` · ${r.serviceCategoryName}` : ''}
                    </span>
                    <span>{fmtMoney(r.defaultRate)}</span>
                  </div>
                  {hasJurisdiction && (
                    <div style={{ fontSize: '0.78rem' }}>
                      {r.taxMapping?.rateCode
                        ? <span style={{ color: 'var(--text-secondary)' }}>{r.taxMapping.rateName || r.taxMapping.rateCode}</span>
                        : <span className="badge" style={{ background: 'var(--danger-bg, #fee2e2)', color: 'var(--danger, #b91c1c)' }}>Unmapped</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <ServiceMasterViewModal
        isOpen={viewOpen}
        service={viewService}
        onClose={() => { setViewOpen(false); setViewService(null); }}
        onEdit={can('UPDATE') ? (s) => { setViewOpen(false); setViewService(null); handleEdit(s); } : undefined}
        tabId={tabId}
        moduleName={moduleName || 'Services'}
        onRestoreView={handleRestoreView}
      />

      <ServiceFormModal isOpen={formOpen} mode={formMode} record={selected}
        onSaved={refetch}
        onClose={() => { setFormOpen(false); setSelected(null); }}
        tabId={tabId} moduleName={moduleName || 'Services'}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal} />
    </EnterpriseScreenShell>
  );
};

export default ServiceMasterScreen;
